import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'trident_auth';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const loadFromStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (data.expiresAt && Date.now() > data.expiresAt) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return data;
    } catch {
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(loadFromStorage);

    const isAuthenticated = useMemo(() => {
        if (!auth?.token) return false;
        if (auth.expiresAt && Date.now() > auth.expiresAt) return false;
        return true;
    }, [auth]);

    const login = useCallback((authData) => {
        const authState = {
            token: authData.token,
            username: authData.username,
            roles: authData.roles ?? [],
            expiresAt: Date.now() + authData.expiresIn,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(authState));
        setAuth(authState);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setAuth(null);
    }, []);

    useEffect(() => {
        // Attach Authorization header to every outgoing request
        const requestInterceptor = axios.interceptors.request.use((config) => {
            const stored = loadFromStorage();
            if (stored?.token) {
                config.headers = config.headers ?? {};
                config.headers.Authorization = `Bearer ${stored.token}`;
            }
            return config;
        });

        // On 401 from any protected endpoint, clear auth and redirect to login
        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const isAuthEndpoint = error.config?.url?.includes('/api/auth/');
                if (error.response?.status === 401 && !isAuthEndpoint) {
                    localStorage.removeItem(STORAGE_KEY);
                    setAuth(null);
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    const value = useMemo(
        () => ({ auth, isAuthenticated, login, logout }),
        [auth, isAuthenticated, login, logout]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
